import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ClientHeader } from '../components/client/ClientHeader';
import { LoadingCard, EmptyState } from '../components/client/clientShared';
import AnimalDialog from '../components/animals/AnimalDialog';
import { ANIMAL_LABELS, SEX_LABELS } from '../lib/animals';
import { PawPrint, Plus, Edit2, Trash2, Camera, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientAnimalsPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [animals, setAnimals] = useState([]);
  const [animalDialogOpen, setAnimalDialogOpen] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState(null);
  const [photoUrls, setPhotoUrls] = useState({});
  const [photoBusy, setPhotoBusy] = useState(null);
  const photoInputRef = useRef(null);
  const photoTargetRef = useRef(null);

  const reload = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API_URL}/api/client/animals`, { headers });
      setAnimals(res.data || []);
    } catch (err) {
      toast.error('Could not load your animals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch animal photos as authenticated blobs
  useEffect(() => {
    const withPhoto = animals.filter((a) => a.photo?.id && !photoUrls[a.id]);
    if (withPhoto.length === 0) return;
    const headers = { Authorization: `Bearer ${token}` };
    (async () => {
      for (const a of withPhoto) {
        try {
          const res = await axios.get(`${API_URL}/api/client/animals/${a.id}/photo`, { headers, responseType: 'blob' });
          setPhotoUrls((prev) => ({ ...prev, [a.id]: URL.createObjectURL(res.data) }));
        } catch { /* ignore */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animals]);

  const pickPhoto = (a) => {
    photoTargetRef.current = a;
    photoInputRef.current?.click();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const a = photoTargetRef.current;
    if (!file || !a) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo too large (max 5MB)'); return; }
    setPhotoBusy(a.id);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/client/animals/${a.id}/photo`, fd, { headers });
      setPhotoUrls((prev) => ({ ...prev, [a.id]: undefined }));
      toast.success(`Photo saved for ${a.name}`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setPhotoBusy(null);
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Remove "${a.name}" from your saved animals?`)) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_URL}/api/client/animals/${a.id}`, { headers });
      toast.success('Animal removed');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove animal');
    }
  };

  const openAdd = () => { setEditingAnimal(null); setAnimalDialogOpen(true); };
  const openEdit = (a) => { setEditingAnimal(a); setAnimalDialogOpen(true); };

  return (
    <div className="min-h-screen bg-background">
      <ClientHeader title="My Animals" subtitle="Saved animals reusable across providers" backTo="/client/dashboard" />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-sm text-muted-foreground">Enter details once, reuse them on every booking.</p>
          <Button onClick={openAdd} data-testid="add-animal-btn"><Plus className="h-4 w-4 mr-1" /> Add Animal</Button>
        </div>
        <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="animal-photo-input" />

        {loading ? <LoadingCard /> : animals.length === 0 ? (
          <EmptyState icon={<PawPrint className="h-10 w-10 opacity-40" />} title="No saved animals yet"
            subtitle="Add your animals once and reuse them when booking with any provider."
            cta="Add Your First Animal" onCta={openAdd} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {animals.map((a) => (
              <Card key={a.id} data-testid={`animal-${a.id}`}>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3 mb-1">
                    <button
                      type="button"
                      onClick={() => pickPhoto(a)}
                      className="relative h-12 w-12 rounded-full overflow-hidden border bg-muted flex items-center justify-center shrink-0 group"
                      data-testid={`animal-photo-btn-${a.id}`}
                      aria-label={`Add photo for ${a.name}`}
                    >
                      {photoBusy === a.id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ) : a.photo?.id && photoUrls[a.id] ? (
                        <img src={photoUrls[a.id]} alt={a.name} className="h-full w-full object-cover" data-testid={`animal-photo-${a.id}`} />
                      ) : a.photo?.id ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ) : (
                        <PawPrint className="h-5 w-5 text-primary" />
                      )}
                      <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Camera className="h-4 w-4 text-white" />
                      </span>
                    </button>
                    <h3 className="font-semibold truncate">{a.name}</h3>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    <Badge variant="secondary" className="text-xs">{ANIMAL_LABELS[a.animalType] || a.animalType}</Badge>
                    {a.sex && <Badge variant="outline" className="text-xs">{SEX_LABELS[a.sex] || a.sex}</Badge>}
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    {a.breed && <p>Breed: {a.breed}</p>}
                    {a.age != null && <p>Age: {a.age}</p>}
                    {a.subType && <p>Type: {a.subType}</p>}
                    {a.handlingNotes && <p className="text-xs italic line-clamp-2"><strong>Handling:</strong> {a.handlingNotes}</p>}
                    {a.behaviorNotes && <p className="text-xs italic line-clamp-2"><strong>Behavior:</strong> {a.behaviorNotes}</p>}
                    {a.medicalNotes && <p className="text-xs italic line-clamp-2"><strong>Medical:</strong> {a.medicalNotes}</p>}
                  </div>
                  <div className="flex gap-2 mt-3 pt-3 border-t">
                    <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`edit-animal-${a.id}`}>
                      <Edit2 className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(a)} data-testid={`delete-animal-${a.id}`}>
                      <Trash2 className="h-3 w-3 mr-1" /> Remove
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <AnimalDialog open={animalDialogOpen} onOpenChange={setAnimalDialogOpen} animal={editingAnimal} onSaved={reload} />
    </div>
  );
}
